import React, { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useMediaQuery, useTheme } from '@mui/material';
import { useQuery } from '@apollo/client';

import { Modal, ResponsiveWidth } from '../../../../common';
import { useStyles } from './barcode-modal.styles';
import { Barcode } from '../../../../common/barcode';
import { IBarcodeModalProps } from '../barcode.models';
import { consumerApi } from '../../../../../rest/consumer';
import { Button } from '../../../../common/button-new';
import { ReactComponent as Wallet } from '../../../../../assets/images/wallet-menu.svg';
import { ReactComponent as Circle } from '../../../../../assets/images/circled-sync-back.svg';
import chevron from '../../../../../assets/images/icons/arrow/chevron-right.svg';
import { CONSUMER_WALLET_QUERY } from '../../../../../graphql/wallet/consumerWallet';
import { ConsumerWalletData } from '../../../../../graphql/wallet/models/consumer-wallet.model';

export const BarcodeModal = (props: IBarcodeModalProps): JSX.Element => {
  const classes = useStyles();
  const { myAcuvueId, modalProps } = props;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const responsiveWidth = isMobile ? ResponsiveWidth.lg : ResponsiveWidth.mdWider;
  const intl = useIntl();

  const { data: walletData } = useQuery<ConsumerWalletData>(CONSUMER_WALLET_QUERY);
  const walletCardLink = walletData?.consumerWallet?.walletCardLink;

  const hasValidWalletLink = typeof walletCardLink === 'string' && walletCardLink.trim().length > 0;

  const [otpCode, setOtpCode] = useState<string | null>(null);
  const [cooldown, setCooldown] = useState<number>(0);
  const [animationKey, setAnimationKey] = useState(0);
  const [deadline, setDeadline] = useState<Date | null>(null);
  const [remainingSeconds, setRemainingSeconds] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      if (deadline) {
        const diff = Math.max(0, Math.ceil((deadline.getTime() - Date.now()) / 1000));
        setRemainingSeconds(diff);
      }
    }, 500);

    return () => clearInterval(interval);
  }, [deadline]);

  useEffect(() => {
    if (!modalProps.open || !myAcuvueId) return;

    consumerApi
      .inAppOtpRequest({ force: false })
      .then((res) => {
        setOtpCode(res.data?.code);
      })
      .catch((err) => {
        console.error(err);
      });

    const interval = setInterval(() => {
      consumerApi
        .inAppOtpRequest({ force: false })
        .then((res) => {
          setOtpCode(res.data?.code);
        })
        .catch((err) => console.error(err));
    }, 50000);

    return () => clearInterval(interval);
  }, [modalProps.open, myAcuvueId]);

  useEffect(() => {
    if (cooldown > 0) {
      const intervalId = setInterval(() => {
        setCooldown((prev) => {
          if (prev <= 1) {
            clearInterval(intervalId);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(intervalId);
    }
  }, [cooldown]);

  const handleResendClick = async (): Promise<void> => {
    try {
      const res = await consumerApi.inAppOtpRequest({ force: true });
      setOtpCode(res.data?.code);

      const cooldownDuration = 10 * 1000;
      setDeadline(new Date(Date.now() + cooldownDuration));

      setAnimationKey((prev) => prev + 1);
    } catch (err) {
      console.error(err);
    }
  };

  const handleWalletClick = (): void => {
    if (hasValidWalletLink && walletCardLink) {
      window.open(walletCardLink, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <Modal
      {...modalProps}
      responsiveWidth={responsiveWidth}
      propsContentStyles={classes.propsContentStyles}
      aria-labelledby="otp-modal-title"
      PaperProps={{ className: classes.paper }}
      content={
        <>
          {myAcuvueId && (
            <>
              <h2 className={classes.otpTitle} id="otp-modal-title">
                <FormattedMessage id="barcode.modal.title" />
              </h2>
              <p className={classes.otpSubtitle}>
                {intl.formatMessage(
                  { id: 'barcode.modal.text' },
                  { sup: (chunks: React.ReactNode) => <sup>{chunks}</sup> },
                )}
              </p>
              <div className={classes.otpWrapper}>
                {Array.from({ length: 4 }).map((_, index) => {
                  const digit = otpCode?.[index] ?? '';
                  return (
                    <div key={`${animationKey}-${index}`} className={classes.otpDigit}>
                      <span className={classes.digitText}>{digit}</span>
                    </div>
                  );
                })}
              </div>
              <div className={classes.resendWrapper}>
                {remainingSeconds > 0 ? (
                  <p className={classes.cooldownText}>
                    <FormattedMessage id="barcode.reSendCode.timer" />:
                    {remainingSeconds.toString().padStart(2, '0')}
                  </p>
                ) : (
                  <Button
                    style="text"
                    onClick={handleResendClick}
                    startIcon={<Circle />}
                    onKeyDown={(e: { key: string }) => {
                      if (e.key === 'Enter' || e.key === ' ') handleResendClick();
                    }}
                  > 
                    {/* Don't remove data-testid, it is used by Yandex Metrica */ }
                    <span className={classes.text} data-testid="resend-button">
                      <FormattedMessage id="barcode.reSendCode.text" />
                    </span>
                  </Button>
                )}
              </div>
              <div className={classes.divider} />
              <h2 className={classes.otpTitle}>
                {intl.formatMessage(
                  { id: 'barcode.userId' },
                  { sup: (chunks: React.ReactNode) => <sup>{chunks}</sup> },
                )}
              </h2>
              <p className={classes.hint}>
                <FormattedMessage id="barcode.modal.id" />
              </p>
              <p className={classes.id}>{myAcuvueId}</p>
              <Barcode value={myAcuvueId} className={classes.barcode} />
              {hasValidWalletLink && (
                <>
                  <div className={classes.divider} />
                  <button className={classes.walletButton} onClick={handleWalletClick}>
                    <div className={classes.walletIcon}>
                      <Wallet />
                    </div>
                    <div className={classes.walletContent}>
                      <span className={classes.walletTitle}>
                        {intl.formatMessage(
                          { id: 'barcode.wallet.title' },
                          { sup: (chunks: React.ReactNode) => <sup>{chunks}</sup> },
                        )}
                      </span>
                    </div>
                    <img src={chevron} alt="" className={classes.chevronIcon} />
                  </button>
                  <p className={classes.walletDescription}>
                    {intl.formatMessage(
                      { id: 'barcode.wallet.description' },
                      { sup: (chunks: React.ReactNode) => <sup>{chunks}</sup> },
                    )}
                  </p>
                </>
              )}
            </>
          )}
        </>
      }
    />
  );
};
